import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { CardTransactionRequest } from '@donation/donations/models/donations.request';
import { DonationResponse } from '@donation/donations/models/donations.response';
import { AddressRequest } from '@donation/donors/models/donor-management.request';
import { ApiAppConfigResponse, LoyaltyTierConfigResponse } from '@donation/settings/models/settings.response';
import { environment } from '@env/environment';
import { Observable, catchError, filter, forkJoin, map, of, switchMap, take, throwError } from 'rxjs';

import { ApiStatus, PaginatedResult } from '@core/models/api.enum';
import {
  ContainerStatus,
  ContainerStatusLabel,
  ContainerType,
  ContainerTypeLabel,
  DonationMethod,
  DonationScope,
  DonationStatus,
  DonationStatusLabel,
  DonorTier,
  MeasurementType,
  PaymentMethod,
  ReceiptDelivery,
  UnitType
} from '@core/models/domain.enum';
import { Container, DonationDepartment, Donor, ScheduledDonation } from '@core/models/domain.models';
import { AppSwitcherService } from '@core/services/app-switcher.service';
import { CompanyApiService } from '@core/services/company-api.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { DonationApiService } from '@core/services/donation-api.service';

import { AddressFormData } from '@shared/components/donor-form-fields/donor-form-fields.component';
import { IconName } from '@shared/components/icon/icon.component';

import {
  mapApiContainerToContainer,
  mapApiDonationToScheduledDonation,
  mapApiDonorToDonor,
  mapApiDonorToSelected,
  mapSharedDepartmentsToDonationDepartments
} from '../models/new-donation.mapper';
import { SharedDepartmentItem } from '../models/new-donation.response';
import { EnrollForm, SelectedDonor, SelectedItemsMap } from '../models/new-donation.state';

@Injectable({ providedIn: 'root' })
export class NewDonationService {
  private readonly donationApi = inject(DonationApiService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly appSwitcher = inject(AppSwitcherService);
  private readonly http = inject(HttpClient);

  private readonly _storeConfig = signal<Pick<
    ApiAppConfigResponse,
    | 'isCashAccepted'
    | 'pointsPerItem'
    | 'pointsPerDollar'
    | 'emailForLogin'
    | 'pointsPerBox'
    | 'pointsPerBag'
    | 'pointsPerLb'
  > | null>(null);

  private readonly _loyaltyTiers = signal<LoyaltyTierConfigResponse[]>([]);

  private readonly storeId$ = toObservable(this.currentUser.storeId);

  get departments(): DonationDepartment[] {
    return [];
  }

  loadStoreConfig(): Observable<void> {
    return this.storeId$.pipe(
      filter(id => id > 0),
      take(1),
      switchMap(id =>
        forkJoin({
          cfg: this.donationApi.getStoreConfig(id).pipe(catchError(() => of(null))),
          tiers: this.donationApi.getLoyaltyTiers(this.currentUser.companyId()).pipe(catchError(() => of([])))
        }).pipe(
          map(({ cfg, tiers }) => {
            if (cfg) {
              this._storeConfig.set({
                isCashAccepted: cfg.isCashAccepted,
                pointsPerItem: cfg.pointsPerItem,
                pointsPerDollar: cfg.pointsPerDollar,
                emailForLogin: cfg.emailForLogin,
                pointsPerBox: cfg.pointsPerBox ?? 30,
                pointsPerBag: cfg.pointsPerBag ?? 30,
                pointsPerLb: cfg.pointsPerLb ?? 2
              });
            }
            this._loyaltyTiers.set(tiers as LoyaltyTierConfigResponse[]);
          })
        )
      )
    );
  }

  getDepartments(): Observable<DonationDepartment[]> {
    const params = new HttpParams().set('ClientSidePagination', 'true');
    return this.http
      .get<PaginatedResult<SharedDepartmentItem>>(`${environment.donationApiUrl}/SharedDepartments`, { params })
      .pipe(
        map(res => mapSharedDepartmentsToDonationDepartments(res.items)),
        catchError(error => throwError(() => error))
      );
  }

  validateConfig(): Observable<{ isValid: boolean; validationMessages: string[]; isAcceptDonations: boolean }> {
    return this.donationApi.validateConfig({
      StoreId: this.currentUser.storeId(),
      CompanyId: this.currentUser.companyId()
    });
  }

  isCashAccepted(): boolean {
    return this._storeConfig()?.isCashAccepted ?? false;
  }

  pointsPerItem(): number {
    return this._storeConfig()?.pointsPerItem ?? 0;
  }

  pointsPerBox(): number {
    return this._storeConfig()?.pointsPerBox ?? 30;
  }

  pointsPerBag(): number {
    return this._storeConfig()?.pointsPerBag ?? 30;
  }

  pointsPerLb(): number {
    return this._storeConfig()?.pointsPerLb ?? 2;
  }

  pointsPerDollar(): number {
    return this._storeConfig()?.pointsPerDollar ?? 1;
  }

  emailForLogin(): boolean {
    return this._storeConfig()?.emailForLogin ?? false;
  }

  newReceipt(): string {
    return `DN-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`;
  }

  getTierMultiplier(tier: DonorTier | undefined): number {
    if (!tier) return 1;
    const found = this._loyaltyTiers().find(t => t.tier === tier);
    return found?.pointsMultiplier ?? 1;
  }

  getTierConfig(tier: DonorTier): { icon: IconName; label: string } {
    const label = this._loyaltyTiers().find(t => t.tier === tier)?.label ?? String(tier);
    const iconMap: Record<DonorTier, IconName> = {
      [DonorTier.Bronze]: 'check-circle',
      [DonorTier.Silver]: 'trending-up',
      [DonorTier.Gold]: 'star',
      [DonorTier.Platinum]: 'gift'
    };
    return { icon: iconMap[tier] ?? 'star', label };
  }

  searchDonors(query: string): Observable<SelectedDonor[]> {
    if (query.length < 2) return of([]);
    return this.donationApi
      .getDonors({ CompanyId: this.currentUser.companyId(), SearchTerm: query, ClientSidePagination: true })
      .pipe(
        map(result => result.items.map(mapApiDonorToSelected)),
        catchError(error => throwError(() => error))
      );
  }

  /** Search for a donor by scanned QR reference number. */
  searchDonorByReference(referenceNumber: string): Observable<SelectedDonor | null> {
    return this.donationApi
      .getDonors({ CompanyId: this.currentUser.companyId(), SearchTerm: referenceNumber, ClientSidePagination: true })
      .pipe(
        map(result => (result.items.length ? mapApiDonorToSelected(result.items[0]) : null)),
        catchError(error => throwError(() => error))
      );
  }

  getAvailableContainers(query: string): Observable<Container[]> {
    return this.donationApi
      .getContainers({
        CompanyId: this.currentUser.companyId(),
        Status: ContainerStatus.Available,
        SearchTerm: query || undefined,
        ClientSidePagination: true
      })
      .pipe(
        map(result => result.items.map(mapApiContainerToContainer)),
        catchError(error => throwError(() => error))
      );
  }

  lookupScheduledDonations(query: string): Observable<ScheduledDonation[]> {
    if (!query.trim()) return of([]);
    return this.donationApi
      .getDonations({
        CompanyId: this.currentUser.companyId(),
        Status: DonationStatus.Scheduled,
        SearchTerm: query.trim(),
        ClientSidePagination: true
      })
      .pipe(
        map(result => result.items.map(mapApiDonationToScheduledDonation)),
        catchError(error => throwError(() => error))
      );
  }

  getFirstScheduledDonation(): Observable<ScheduledDonation | null> {
    return this.donationApi
      .getDonations({ CompanyId: this.currentUser.companyId(), Status: DonationStatus.Scheduled, PageSize: 1 })
      .pipe(
        map(result => (result.items.length ? mapApiDonationToScheduledDonation(result.items[0]) : null)),
        catchError(error => throwError(() => error))
      );
  }

  findDonorById(donorId: number): Observable<Donor | undefined> {
    return this.donationApi.getDonor(donorId).pipe(
      map(d => (d ? mapApiDonorToDonor(d) : undefined)),
      catchError(error => throwError(() => error))
    );
  }

  findDonorByIdAsSelected(donorId: number): Observable<SelectedDonor | undefined> {
    return this.donationApi.getDonor(donorId).pipe(
      map(d => (d ? mapApiDonorToSelected(d) : undefined)),
      catchError(() => of(undefined))
    );
  }

  /**
   * Loads the full DonationResponse for a scheduled donation.
   * Used by Load & Continue so it follows the exact same path as "Check In & Start Donation".
   * Returns null in mock mode when the scheduled donation is not in the donations list
   * (mock scheduled appointments are a separate list from mock DonationResponse objects).
   */
  loadScheduledDonationById(id: number): Observable<DonationResponse | null> {
    return this.donationApi.getDonation(id).pipe(
      map(d => d ?? null),
      catchError(error => throwError(() => error))
    );
  }

  getRandomDonor(): Observable<Donor | null> {
    return of(null);
  }

  associateDonorToDonation(donationId: number, donorId: number): Observable<void> {
    return this.donationApi.associateDonation(donationId, { donorId }).pipe(
      map(() => undefined),
      catchError(error => throwError(() => error))
    );
  }

  containerTypeLabel(t: ContainerType): string {
    return ContainerTypeLabel[t] ?? String(t);
  }

  containerStatusLabel(s: ContainerStatus): string {
    return ContainerStatusLabel[s] ?? String(s);
  }

  donationStatusLabel(s: DonationStatus): string {
    return DonationStatusLabel[s] ?? String(s);
  }

  enrollDonor(form: EnrollForm): Observable<SelectedDonor> {
    const userName = form.email || undefined;
    return this.companyApi
      .registerDonorUser({
        companyId: this.currentUser.companyId(),
        phoneNumber: form.phone,
        email: form.email || undefined,
        firstName: form.firstName,
        lastName: form.lastName,
        userName,
        userType: 1
      })
      .pipe(
        switchMap(userRes =>
          this.donationApi.createDonor({
            companyId: this.currentUser.companyId(),
            userId: userRes.id,
            loyaltyTier: DonorTier.Bronze,
            preferredStoreId: this.currentUser.storeId(),
            joinDate: new Date().toISOString().split('T')[0],
            status: ApiStatus.Active,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email || undefined,
            phoneNumber: form.phone,
            address: toAddressRequest(form.address),
            userName
          })
        ),
        map(d => mapApiDonorToSelected({ ...d, firstName: form.firstName, lastName: form.lastName })),
        catchError(error => throwError(() => error))
      );
  }

  get storeId(): number {
    return this.currentUser.storeId();
  }

  createDonation(donation: {
    donorId?: number | null;
    storeId: number;
    method: DonationMethod;
    scope: DonationScope;
    items: {
      departmentId?: number | null;
      categoryId?: number | null;
      departmentKey?: string | null;
      categoryKey?: string | null;
      subCategoryKey?: string | null;
      quantity: number;
      estimatedValuePerItem?: number;
    }[];
    monetaryAmount?: number;
    paymentMethod?: PaymentMethod | null;
    cashTendered?: number | null;
    cardTxnRef?: string | null;
    cardTransaction?: CardTransactionRequest | null;
    receiptDelivery?: ReceiptDelivery | null;
    notes?: string | null;
    loyaltyPointsEarned?: number | null;
  }): Observable<{ id: number; referenceNumber: string; receiptId: number | null }> {
    return this.donationApi
      .createDonation({
        companyId: Number(this.currentUser.companyId()),
        storeId: donation.storeId,
        attendantId: Number(this.currentUser.userId()) || null,
        donorId: donation.donorId ?? null,
        status: DonationStatus.Completed,
        method: donation.method,
        scope: donation.scope,
        loyaltyPointsEarned: donation.loyaltyPointsEarned ?? null,
        receiptDelivery: donation.receiptDelivery ?? null,
        notes: donation.notes ?? null,
        cardTransaction: donation.cardTransaction ?? null,
        items: [
          ...donation.items.map(item => {
            const itemScope =
              donation.scope === DonationScope.Monetary
                ? DonationScope.Monetary
                : donation.scope === DonationScope.Box
                  ? DonationScope.Box
                  : donation.scope === DonationScope.Bag
                    ? DonationScope.Bag
                    : donation.scope === DonationScope.ByWeight
                      ? DonationScope.ByWeight
                      : DonationScope.Items;
            const unitType =
              itemScope === DonationScope.ByWeight
                ? UnitType.Lbs
                : itemScope === DonationScope.Monetary
                  ? UnitType.Amount
                  : UnitType.PerUnit;
            const measurementType =
              itemScope === DonationScope.ByWeight
                ? MeasurementType.Weight
                : itemScope === DonationScope.Monetary
                  ? MeasurementType.Amount
                  : MeasurementType.Unit;
            return {
              scope: itemScope,
              unitType,
              measurementType,
              departmentId: item.departmentId ?? null,
              categoryId: item.categoryId ?? null,
              departmentKey: item.departmentKey ?? null,
              categoryKey: item.categoryKey ?? null,
              subCategoryKey: item.subCategoryKey ?? null,
              quantity: item.quantity,
              estimatedValuePerItem: item.estimatedValuePerItem ?? null
            };
          }),
          ...(donation.monetaryAmount !== undefined && donation.monetaryAmount > 0
            ? [
                {
                  scope: DonationScope.Monetary,
                  amount: donation.monetaryAmount,
                  paymentMethod: donation.paymentMethod ?? null,
                  cashTendered: donation.cashTendered ?? null,
                  changeGiven:
                    donation.cashTendered !== null &&
                    donation.cashTendered !== undefined &&
                    donation.monetaryAmount !== undefined
                      ? donation.cashTendered - donation.monetaryAmount
                      : null,
                  cardTxnRef: donation.cardTxnRef ?? null
                }
              ]
            : [])
        ]
      })
      .pipe(
        map(res => ({ id: res.id, referenceNumber: res.referenceNumber, receiptId: res.receiptId ?? null })),
        catchError(error => throwError(() => error))
      );
  }

  /**
   * Runs the receipt upload chain (Steps 2–4 from the complete-donation-process doc):
   * GET receipt PDF → POST BlobFiles/Upload → PUT Donations/{receiptId}/UpdateReceipt
   * Errors short-circuit the chain per the abort-on-failure requirement.
   */
  runReceiptUploadChain(donationId: number, receiptId: number): Observable<void> {
    return this.donationApi.getDonationReceiptPdf(donationId).pipe(
      switchMap(blob => this.donationApi.uploadReceiptBlob(donationId, this.currentUser.companyId(), blob)),
      switchMap(uploadResult => {
        if (!uploadResult.isSuccess || !uploadResult.uploadedFiles?.length) {
          return throwError(() => new Error('Receipt upload failed'));
        }
        const blobFileId = uploadResult.uploadedFiles[0].blobFileId;
        return this.donationApi.updateDonationReceipt(receiptId, blobFileId);
      }),
      catchError(error => throwError(() => error))
    );
  }

  changeDonationStatus(donationId: number, status: DonationStatus, reason?: string): Observable<boolean> {
    return this.donationApi
      .changeDonationStatus(donationId, {
        transitionTo: status,
        reason: reason ?? null
      })
      .pipe(catchError(error => throwError(() => error)));
  }

  updateCheckInDonation(
    id: number,
    snapshot: DonationResponse,
    selectedItems: SelectedItemsMap,
    loyaltyPointsEarned: number,
    departments: DonationDepartment[] = []
  ): Observable<void> {
    // Physical items from current wizard selections; existing items keep their id, new items get id 0
    const physicalItems = Object.entries(selectedItems).map(([key, entry]) => {
      const parts = key.split('::');
      const deptKey = parts[0] ?? null;
      const catKey = parts[1] ?? null;
      const subKey = parts[2] ?? null;
      const dept = deptKey ? departments.find(d => d.key === deptKey) : undefined;
      const cat = catKey ? dept?.categories.find(c => c.key === catKey) : undefined;
      const sub = subKey && cat ? cat.subCategories?.find(s => s.key === subKey) : undefined;
      const deptName = dept?.name ?? deptKey;
      const catName = cat?.name ?? catKey;
      const subName = sub?.name ?? subKey;
      const original = (snapshot.items ?? []).find(
        i =>
          i.scope === DonationScope.Items &&
          ((i.departmentKey ?? null) === deptKey || (i.departmentKey ?? null) === deptName) &&
          ((i.categoryKey ?? null) === catKey || (i.categoryKey ?? null) === catName) &&
          ((i.subCategoryKey ?? null) === subKey || (i.subCategoryKey ?? null) === subName)
      );
      return {
        id: original?.id ?? 0,
        donationId: id,
        scope: DonationScope.Items,
        departmentId: deptKey ? Number(deptKey) || null : null,
        categoryId: catKey ? Number(catKey) || null : null,
        departmentKey: deptName,
        categoryKey: catName,
        subCategoryKey: subName,
        quantity: entry.qty,
        estimatedValuePerItem: entry.estimatedValue ?? null
      };
    });

    // Monetary items from snapshot; existing items keep their id
    const monetaryItems = (snapshot.items ?? [])
      .filter(i => i.scope === DonationScope.Monetary)
      .map(i => ({
        id: i.id,
        donationId: i.donationId,
        scope: i.scope,
        amount: i.amount ?? null,
        paymentMethod: i.paymentMethod ?? null,
        cardTxnRef: i.cardTxnRef ?? null,
        cashTendered: i.cashTendered ?? null,
        changeGiven: i.changeGiven ?? null
      }));

    return this.donationApi
      .updateDonation(id, {
        id: snapshot.id,
        companyId: snapshot.companyId,
        storeId: snapshot.storeId,
        attendantId: Number(this.currentUser.userId()) || null,
        donorId: snapshot.donorId ?? null,
        status: DonationStatus.Completed,
        method: snapshot.method,
        scope: snapshot.scope,
        loyaltyPointsEarned: loyaltyPointsEarned,
        receiptDelivery: snapshot.receiptDelivery ?? null,
        notes: snapshot.notes ?? null,
        items: [...physicalItems, ...monetaryItems]
      })
      .pipe(
        map(() => undefined),
        catchError(error => throwError(() => error))
      );
  }

  /**
   * Fetches the authoritative donation + donor data from the API in parallel,
   * called after createDonation/updateDonation succeeds to enrich the receipt.
   * Returns only the fields that differ from (or supplement) local page state.
   */
  fetchReceiptData(
    donationId: number,
    donorId: number | null
  ): Observable<{
    donationAt: Date | null;
    confirmedLoyaltyPoints: number | null;
    confirmedStoreName: string | null;
    confirmedOrgName: string | null;
    donorEmail: string | null;
    donorPhone: string | null;
    donorAddress: string | null;
  }> {
    const donation$ = this.donationApi.getDonation(donationId).pipe(catchError(() => of(null)));
    const donor$ = donorId ? this.donationApi.getDonor(donorId).pipe(catchError(() => of(null))) : of(null);
    return forkJoin({ d: donation$, dr: donor$ }).pipe(
      map(({ d, dr }) => ({
        donationAt: d ? new Date(d.donationAt) : null,
        confirmedLoyaltyPoints: d?.loyaltyPointsEarned ?? null,
        confirmedStoreName: d?.store?.name ?? null,
        confirmedOrgName: d?.company?.name ?? null,
        donorEmail: dr?.email ?? null,
        donorPhone: dr?.phoneNumber ?? (dr as { phone?: string })?.phone ?? null,
        donorAddress: dr?.address
          ? typeof dr.address === 'string'
            ? dr.address || null
            : [
                dr.address.line1,
                dr.address.city,
                dr.address.zipCode,
                dr.address.stateName,
                abbreviateCountryName(dr.address.countryName)
              ]
                .filter(Boolean)
                .join(', ') || null
          : null
      }))
    );
  }
}

const COUNTRY_ABBREVIATIONS: Record<string, string> = {
  'united states of america': 'US',
  'united states': 'US',
  'united kingdom': 'UK',
  canada: 'CA',
  australia: 'AU',
  india: 'IN'
};

function abbreviateCountryName(name: string | undefined): string | undefined {
  if (!name) return undefined;
  return COUNTRY_ABBREVIATIONS[name.toLowerCase()] ?? name;
}

/** Converts AddressFormData to the structured AddressRequest object expected by the Donation API. */
function toAddressRequest(addr?: AddressFormData): AddressRequest | undefined {
  if (!addr || (!addr.line1.trim() && !addr.city.trim())) return undefined;
  return {
    id: 0,
    line1: addr.line1.trim(),
    line2: addr.line2?.trim() || undefined,
    city: addr.city.trim(),
    zipCode: addr.zipCode.trim(),
    stateName: addr.stateName.trim(),
    countryName: addr.countryName.trim(),
    latitude: 0,
    longitude: 0,
    type: 1
  };
}
